/**
 * Widget data pipeline (field-wells model).
 *
 * A counter is a single scalar. A chart has field wells — X axis, Bars measures,
 * Lines measures, and an optional Group/color dimension — rendered as a combo
 * (stacked/clustered bars + lines on an optional secondary axis). Bar-vs-line is
 * decided by which well a measure sits in.
 *
 * Aggregation reuses the production engine (buildAggregationQuery +
 * flattenAggBuckets). Series logic (alternatives): with a Group dimension the bar
 * series are the group values (single bar measure) and lines aggregate at the X
 * level via a second query; without a group, each measure is its own series and a
 * single X-grouped query serves both bars and lines.
 */

import { OpenSearchQueryBuilder } from 'tio-common/utils/open-search-builder';
import type { FilterClause } from 'tio-common/utils/open-search-builder';
import { buildAggregationQuery, camelToSnake } from '../aggregation-query-builder.ts';
import { flattenAggBuckets } from '../aggregation-response-parser.ts';
import type {
  AggregationType,
  ColumnAggregationMap,
  DateGroupingInterval,
  DateGroupingMap,
} from '../aggregation-types.ts';
import type { FieldOption } from '../opensearch-field-mapper.ts';
import { getEnumFormatForField } from '../enum-label-registry.ts';
import { formatEnumValue } from '../column-format-options.ts';
import {
  PIE_SLICE_LIMIT,
  type BarMode,
  type LinesAxis,
  type WidgetType,
} from './widget-config-types.ts';
import { searchRequestFor } from '../search-requests.ts';
import { isSqlDataset, isCrossCompanyCapable } from '../dataset-utils.ts';
import type { Store as StoreService } from '@warp-drive/core';

/** Palette mirrors tio-ui DEFAULT_CHART_COLORS. */
const SERIES_COLORS = [
  '#0e7490',
  '#6e44c1',
  '#2dd4bf',
  '#998ce3',
  '#2563eb',
  '#059669',
  '#f59e0b',
  '#dc2626',
  '#77bcc0',
  '#7c2d12',
];

/** A resolved measure — the dataset field looked up (null = count of documents). */
export interface ResolvedMeasure {
  id: string;
  field: FieldOption | null;
  aggregation: AggregationType;
  label: string;
}

/** A resolved widget — fields already looked up from the dataset's registry. */
export interface ResolvedWidget {
  widgetType: WidgetType;
  modelType: string;
  // counter
  measureField?: FieldOption | null;
  aggregation?: AggregationType;
  // chart wells
  xDimension?: FieldOption;
  xInterval?: DateGroupingInterval;
  groupDimension?: FieldOption;
  bars?: ResolvedMeasure[];
  lines?: ResolvedMeasure[];
  barMode?: BarMode;
  linesAxis?: LinesAxis;
  // pie
  sliceField?: FieldOption;
  sliceInterval?: DateGroupingInterval;
  donut?: boolean;
  /** Per-series color overrides keyed by series label. */
  colors?: Record<string, string>;
  filterClauses?: FilterClause[];
  companyId?: string | number;
  companyFilterField?: string;
  limit?: number;
}

export interface CounterData {
  type: 'counter';
  value: number;
}
export interface Series {
  label: string;
  data: number[];
  color: string;
}
export interface ComboData {
  type: 'combo';
  labels: string[];
  barSeries: Series[];
  lineSeries: Series[];
  stacked: boolean;
  secondaryAxis: boolean;
}
export interface PiePoint {
  category: string;
  value: number;
}
export interface PieData {
  type: 'pie';
  points: PiePoint[];
  donut: boolean;
}
export type WidgetChartData = CounterData | ComboData | PieData;

/**
 * Execute one aggregation query through the source-kind seam (RFC 062):
 * OpenSearch datasets hit /api/v2/search, SQL datasets /api/v2/sql-search —
 * same `{ 'model-type', searchBody }` payload, same OpenSearch response
 * envelope for our adapt* functions. `companyId` is the SQL tenant param
 * (ignored for OpenSearch payloads, whose scoping lives in the body).
 */
async function runSearch(
  store: StoreService,
  payload: Record<string, unknown>,
  companyId?: string
): Promise<unknown> {
  const { content } = await store.request(searchRequestFor(payload, companyId));
  return content;
}

/**
 * Tenant scoping for SQL-dataset widgets is a request param, never an
 * in-body term (mirrors the report builder's live queries — see
 * report-search-body's scopesByCompany). OpenSearch widgets get undefined
 * and keep their in-body company term.
 */
function sqlCompanyParam(widget: ResolvedWidget): string | undefined {
  if (!isSqlDataset(widget.modelType)) return undefined;
  return normalizeCompanyId(widget.companyId);
}

function normalizeCompanyId(companyId?: string | number) {
  return companyId == null || companyId === '' ? undefined : String(companyId);
}

/**
 * A param-kind SQL dataset is one-company-per-request (RFC 062), so an
 * unscoped dashboard cannot query it at all. The picker disables these on
 * all-companies dashboards, so this only catches configs that predate the
 * scope (a saved widget, or one imported from a report) — but it must be
 * checked on EVERY query path, not just the aggregation pipeline: table
 * widgets bypass runWidget entirely and self-fetch through PreviewTable,
 * where an unscoped SQL query would 4xx and surface as a bare "no data".
 */
export function sqlScopeUnavailable(modelType: string, companyId?: string | number): boolean {
  return (
    isSqlDataset(modelType) && !normalizeCompanyId(companyId) && !isCrossCompanyCapable(modelType)
  );
}

/** Message for the surfaces that render {@link sqlScopeUnavailable} as UI. */
export const SQL_SCOPE_REQUIRED_MESSAGE = 'This SQL dataset requires a company-scoped dashboard.';

/** One aggregation query plus the metadata needed to adapt its response. */
interface QuerySpec {
  payload: Record<string, unknown>;
  groupByKeys: string[];
  columnAggregations: ColumnAggregationMap;
  xKey: string;
  groupKey?: string;
  /** Measures requested in this query and how to render them. */
  measures: Array<{ valueKey: string; label: string; kind: 'bar' | 'line' }>;
}

// ── small helpers ────────────────────────────────────────────────────────────

function aggTypeToOsDsl(aggType: AggregationType): string {
  switch (aggType) {
    case 'sum':
      return 'sum';
    case 'avg':
      return 'avg';
    case 'min':
      return 'min';
    case 'max':
      return 'max';
    case 'cardinality':
      return 'cardinality';
    // `avg` over a boolean is the fraction of true docs (0..1).
    case 'percent_true':
      return 'avg';
    case 'count':
    default:
      return 'value_count';
  }
}

function fieldPathFor(field: FieldOption): string {
  return field.sortPath ?? field.path ?? camelToSnake(field.key);
}

function readAggValue(aggregations: Record<string, unknown>, key: string): number | undefined {
  const agg = (aggregations[key] ?? aggregations[camelToSnake(key)]) as
    { value?: number } | undefined;
  return typeof agg?.value === 'number' ? agg.value : undefined;
}

function readTotal(response: Record<string, unknown>): number {
  const hits = response.hits as { total?: number | { value?: number } } | undefined;
  const total = hits?.total;
  if (typeof total === 'number') return total;
  return typeof total?.value === 'number' ? total.value : 0;
}

function buildBaseBody(widget: ResolvedWidget): Record<string, unknown> {
  const builder = new OpenSearchQueryBuilder();
  builder.setHighlight([]);
  // SQL datasets scope tenancy via the request param (sqlCompanyParam) —
  // never an in-body term, mirroring the report builder's live queries.
  if (
    !isSqlDataset(widget.modelType) &&
    widget.companyId != null &&
    widget.companyId !== '' &&
    widget.companyFilterField
  ) {
    builder.mustTerm(widget.companyFilterField, widget.companyId);
  }
  for (const clause of widget.filterClauses ?? []) builder.addFilter(clause);
  return builder.build() as Record<string, unknown>;
}

/**
 * A display labeler for a dimension's raw category values. Enum fields (e.g. TAS
 * application state) map their DB tokens to the same human-readable labels the
 * report builder uses; every other field is passed through unchanged.
 */
function categoryLabeler(field?: FieldOption): (raw: string) => string {
  let enumFormat;
  try {
    enumFormat = field ? getEnumFormatForField(field.key) : undefined;
  } catch {
    enumFormat = undefined;
  }
  if (!enumFormat) return (raw) => raw;
  // Display formatting must never blank a widget; fall back to the raw token.
  return (raw) => {
    try {
      return formatEnumValue(raw, enumFormat);
    } catch {
      return raw;
    }
  };
}

function uniqueInOrder(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    if (!seen.has(v)) {
      seen.add(v);
      out.push(v);
    }
  }
  return out;
}

/**
 * Build the synthetic agg column + value key for a measure. Keys are positional
 * (`m0`, `m1`, …) rather than measure ids: UUID ids contain `_`+digit and dash
 * sequences that the backend's deep_camelize_keys mangles differently than the
 * response parser's fallback, breaking the agg lookup. A count-of-docs measure
 * has no column (flattenAggBuckets falls back to doc_count for its key).
 */
function measureColumn(
  m: ResolvedMeasure,
  index: number
): { valueKey: string; column?: FieldOption } {
  const valueKey = `m${index}`;
  if (m.field) {
    return { valueKey, column: { ...m.field, key: valueKey } };
  }
  return { valueKey };
}

// ── counter ────────────────────────────────────────────────────────────────

export function buildCounterRequest(widget: ResolvedWidget): Record<string, unknown> {
  const base = buildBaseBody(widget);
  if (widget.measureField && widget.aggregation && widget.aggregation !== 'count') {
    const searchBody = {
      ...base,
      size: 0,
      aggs: {
        metric: {
          [aggTypeToOsDsl(widget.aggregation)]: { field: fieldPathFor(widget.measureField) },
        },
      },
    };
    return { 'model-type': widget.modelType, searchBody };
  }
  return {
    'model-type': widget.modelType,
    searchBody: { ...base, size: 0, track_total_hits: true },
  };
}

export function adaptCounter(widget: ResolvedWidget, response: unknown): CounterData {
  const res = (response ?? {}) as Record<string, unknown>;
  if (widget.measureField && widget.aggregation && widget.aggregation !== 'count') {
    const aggregations = (res.aggregations ?? {}) as Record<string, unknown>;
    return { type: 'counter', value: readAggValue(aggregations, 'metric') ?? 0 };
  }
  return { type: 'counter', value: readTotal(res) };
}

// ── chart ────────────────────────────────────────────────────────────────────

function intervalMap(xKey: string, interval?: DateGroupingInterval): DateGroupingMap | undefined {
  return interval ? { [xKey]: interval } : undefined;
}

/** Build the 1–2 aggregation queries for a chart widget. */
export function buildChartQueries(widget: ResolvedWidget): QuerySpec[] {
  const x = widget.xDimension;
  if (!x) return [];
  const xKey = x.key;
  const groupDim = widget.groupDimension;
  const bars = widget.bars ?? [];
  const lines = widget.lines ?? [];
  const intervals = intervalMap(xKey, widget.xInterval);
  const base = buildBaseBody(widget);

  const makeSpec = (
    groupByKeys: string[],
    extraColumns: FieldOption[],
    measureList: Array<{ m: ResolvedMeasure; kind: 'bar' | 'line' }>,
    groupKey?: string
  ): QuerySpec => {
    const columns: FieldOption[] = [x, ...extraColumns];
    const columnAggregations: ColumnAggregationMap = {};
    const measures: QuerySpec['measures'] = [];
    measureList.forEach(({ m, kind }, index) => {
      const { valueKey, column } = measureColumn(m, index);
      if (column) columns.push(column);
      columnAggregations[valueKey] = m.aggregation;
      measures.push({ valueKey, label: m.label, kind });
    });
    const aggs = buildAggregationQuery(
      groupByKeys,
      columns,
      columnAggregations,
      undefined,
      intervals
    );
    return {
      payload: { 'model-type': widget.modelType, searchBody: { ...base, size: 0, aggs } },
      groupByKeys,
      columnAggregations,
      xKey,
      groupKey,
      measures,
    };
  };

  // With a group dimension, bars split by group (single bar measure); lines, if
  // any, aggregate at the X level via a separate query.
  if (groupDim) {
    const specs: QuerySpec[] = [];
    if (bars.length) {
      specs.push(
        makeSpec([xKey, groupDim.key], [groupDim], [{ m: bars[0]!, kind: 'bar' }], groupDim.key)
      );
    }
    if (lines.length) {
      specs.push(
        makeSpec(
          [xKey],
          [],
          lines.map((m) => ({ m, kind: 'line' as const }))
        )
      );
    }
    return specs;
  }

  // No group — one query at the X level serves every bar and line measure.
  const measureList = [
    ...bars.map((m) => ({ m, kind: 'bar' as const })),
    ...lines.map((m) => ({ m, kind: 'line' as const })),
  ];
  if (!measureList.length) return [];
  return [makeSpec([xKey], [], measureList)];
}

interface SpecResult {
  spec: QuerySpec;
  response: unknown;
}

/** Adapt the query responses into combo chart data. */
export function adaptChartResponses(widget: ResolvedWidget, results: SpecResult[]): ComboData {
  const stacked = widget.barMode !== 'clustered';
  const secondaryAxis = (widget.linesAxis ?? 'secondary') !== 'left';
  const empty: ComboData = {
    type: 'combo',
    labels: [],
    barSeries: [],
    lineSeries: [],
    stacked,
    secondaryAxis,
  };
  if (!results.length) return empty;

  // Flatten every response first so we can compute a shared label order.
  const flattened = results.map(({ spec, response }) => {
    const aggregations = ((response ?? {}) as Record<string, unknown>).aggregations ?? {};
    const rows = flattenAggBuckets(
      aggregations as Record<string, unknown>,
      spec.groupByKeys,
      spec.columnAggregations
    );
    return { spec, rows };
  });

  const labels = uniqueInOrder(
    flattened.flatMap(({ spec, rows }) => rows.map((r) => String(r[spec.xKey] ?? '')))
  );

  const barSeries: Series[] = [];
  const lineSeries: Series[] = [];

  for (const { spec, rows } of flattened) {
    if (spec.groupKey) {
      // Bars split by group value (single bar measure).
      const measure = spec.measures[0];
      if (!measure) continue;
      const stacks = uniqueInOrder(rows.map((r) => String(r[spec.groupKey!] ?? '')));
      const cell = new Map<string, number>();
      for (const r of rows) {
        cell.set(
          `${String(r[spec.xKey])}|${String(r[spec.groupKey!])}`,
          Number(r[measure.valueKey] ?? 0)
        );
      }
      const stackLabeler = categoryLabeler(widget.groupDimension);
      for (const stack of stacks) {
        barSeries.push({
          label: stackLabeler(stack),
          color: '',
          data: labels.map((xv) => cell.get(`${xv}|${stack}`) ?? 0),
        });
      }
    } else {
      // One series per measure at the X level.
      const byX = new Map<string, Record<string, unknown>>();
      for (const r of rows) byX.set(String(r[spec.xKey] ?? ''), r);
      for (const measure of spec.measures) {
        const series: Series = {
          label: measure.label,
          color: '',
          data: labels.map((xv) => Number(byX.get(xv)?.[measure.valueKey] ?? 0)),
        };
        if (measure.kind === 'bar') barSeries.push(series);
        else lineSeries.push(series);
      }
    }
  }

  // Assign colors across all series so bars and lines stay distinct, honoring
  // any per-series override from the widget's format.
  const overrides = widget.colors ?? {};
  [...barSeries, ...lineSeries].forEach((s, i) => {
    s.color = overrides[s.label] ?? SERIES_COLORS[i % SERIES_COLORS.length]!;
  });

  const xLabeler = categoryLabeler(widget.xDimension);
  return {
    type: 'combo',
    labels: labels.map(xLabeler),
    barSeries,
    lineSeries,
    stacked,
    secondaryAxis,
  };
}

// ── pie ────────────────────────────────────────────────────────────────────
const PIE_VALUE_KEY = 'value';

function pieAggInputs(widget: ResolvedWidget): {
  sliceKey: string;
  valueKey: string;
  columns: FieldOption[];
  aggMap: ColumnAggregationMap;
} | null {
  const slice = widget.sliceField;
  if (!slice) return null;
  const columns: FieldOption[] = [slice];
  const aggMap: ColumnAggregationMap = {};
  if (widget.measureField) {
    columns.push({ ...widget.measureField, key: PIE_VALUE_KEY });
    aggMap[PIE_VALUE_KEY] = widget.aggregation ?? 'count';
  } else {
    // count of documents → flatten falls back to doc_count for the key.
    aggMap[PIE_VALUE_KEY] = 'count';
  }
  return { sliceKey: slice.key, valueKey: PIE_VALUE_KEY, columns, aggMap };
}

/** Build the `{ 'model-type', searchBody }` payload for a pie widget. */
export function buildPieRequest(widget: ResolvedWidget): Record<string, unknown> | null {
  const inputs = pieAggInputs(widget);
  if (!inputs) return null;
  const base = buildBaseBody(widget);
  const intervals = widget.sliceInterval ? { [inputs.sliceKey]: widget.sliceInterval } : undefined;
  const aggs = buildAggregationQuery(
    [inputs.sliceKey],
    inputs.columns,
    inputs.aggMap,
    undefined,
    intervals
  );
  return { 'model-type': widget.modelType, searchBody: { ...base, size: 0, aggs } };
}

/** Parse a pie response into slices, rolling the tail past PIE_SLICE_LIMIT into "Other". */
export function adaptPieResponse(widget: ResolvedWidget, response: unknown): PieData {
  const donut = Boolean(widget.donut);
  const inputs = pieAggInputs(widget);
  if (!inputs) return { type: 'pie', points: [], donut };
  const aggregations = ((response ?? {}) as Record<string, unknown>).aggregations ?? {};
  const rows = flattenAggBuckets(
    aggregations as Record<string, unknown>,
    [inputs.sliceKey],
    inputs.aggMap
  );
  const labelFor = categoryLabeler(widget.sliceField);
  let points: PiePoint[] = rows
    .map((r) => ({
      category: labelFor(String(r[inputs.sliceKey] ?? '')),
      value: Number(r[inputs.valueKey] ?? 0),
    }))
    .filter((p) => p.value > 0)
    .sort((a, b) => b.value - a.value);

  if (points.length > PIE_SLICE_LIMIT) {
    const top = points.slice(0, PIE_SLICE_LIMIT);
    const otherValue = points.slice(PIE_SLICE_LIMIT).reduce((sum, p) => sum + p.value, 0);
    if (otherValue > 0) top.push({ category: 'Other', value: otherValue });
    points = top;
  }
  return { type: 'pie', points, donut };
}

/** Build the query/queries, run them, and adapt to chart-ready data. */
export async function runWidget(
  store: StoreService,
  widget: ResolvedWidget
): Promise<WidgetChartData> {
  // Shared with the table path (see sqlScopeUnavailable) so every widget type
  // refuses an unqueryable config the same way.
  if (sqlScopeUnavailable(widget.modelType, widget.companyId)) {
    throw new Error(SQL_SCOPE_REQUIRED_MESSAGE);
  }

  if (widget.widgetType === 'counter') {
    const response = await runSearch(store, buildCounterRequest(widget), sqlCompanyParam(widget));
    return adaptCounter(widget, response);
  }

  if (widget.widgetType === 'pie') {
    const payload = buildPieRequest(widget);
    if (!payload) return { type: 'pie', points: [], donut: Boolean(widget.donut) };
    const response = await runSearch(store, payload, sqlCompanyParam(widget));
    return adaptPieResponse(widget, response);
  }

  const stacked = widget.barMode !== 'clustered';
  const secondaryAxis = (widget.linesAxis ?? 'secondary') !== 'left';
  const empty: ComboData = {
    type: 'combo',
    labels: [],
    barSeries: [],
    lineSeries: [],
    stacked,
    secondaryAxis,
  };

  const specs = buildChartQueries(widget);
  if (!specs.length) return empty;
  const responses = await Promise.all(
    specs.map((s) => runSearch(store, s.payload, sqlCompanyParam(widget)))
  );
  return adaptChartResponses(
    widget,
    specs.map((spec, i) => ({ spec, response: responses[i] }))
  );
}
